import React from "react";
import { motion } from "framer-motion";
import { Check, Headset } from "lucide-react";
import { IMAGES } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { Reveal } from "../components/site/Reveal";
import { ServiceHero, SectionHeading } from "../components/site/ServiceHero";
import { CTABand } from "../components/site/CTABand";
import { LeadForm } from "../components/site/LeadForm";
import { STARTUP_INCLUDES } from "../data/content";

export default function StartCompany() {
  return (
    <div data-testid="startup-page">
      <Seo title="Start a Trucking Company — Complete Startup Package" description="Complete trucking business startup package for $2,000: LLC, EIN, FMCSA registration, USDOT/MC authority, BOC-3, UCR, compliance setup, and 7-days-a-week support." />
      <ServiceHero eyebrow="Start a Trucking Company" title="YOU HAVE THE DREAM." highlight="WE'LL HELP YOU BUILD THE FOUNDATION."
        subtitle="Starting a trucking company comes with questions — lots of them. We'll walk the whole process with you."
        image={IMAGES.blueSkySemi} />

      <section className="relative py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="nm-card rounded-3xl overflow-hidden border-[#D4AF37]/40 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.3)]">
              <div className="nm-gold-bg p-8 text-center">
                <p className="font-mono text-xs tracking-[0.3em] text-[#0B132B]/80 mb-2">COMPLETE TRUCKING BUSINESS START-UP PACKAGE</p>
                <p className="font-head text-6xl font-black text-[#0B132B]">$2,000</p>
              </div>
              <div className="p-8 sm:p-10">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {STARTUP_INCLUDES.map((it, i) => (
                    <motion.div key={it} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                      className="flex items-start gap-3 text-gray-200 text-sm">
                      <Check size={17} className="text-[#10B981] mt-0.5 shrink-0" strokeWidth={3} />
                      {it}
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 rounded-2xl bg-[#2563EB]/10 border border-[#2563EB]/30 p-6 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#2563EB]/20 flex items-center justify-center shrink-0">
                    <Headset className="text-[#3B82F6]" size={20} />
                  </div>
                  <div>
                    <p className="font-head text-lg font-bold text-white">PLUS — 7 DAYS A WEEK SUPPORT</p>
                    <p className="text-gray-400 text-sm mt-1">Clients receive access to guidance throughout the startup process so they aren't left trying to figure everything out alone.</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="mt-16 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionHeading eyebrow="Ready to begin?" className="mb-5">START YOUR <span className="nm-gold-text">FOUNDATION.</span></SectionHeading>
              <p className="text-gray-400 leading-relaxed">
                We'll help you set up the structure, filings, and compliance systems your new authority needs — and coach you so you
                understand your own business every step of the way.
              </p>
              <p className="text-[11px] text-gray-500 leading-relaxed mt-8">
                Government filing fees, insurance premiums, third-party vendor charges, and other outside expenses may be separate unless
                specifically included in the client's written service agreement. Final authority approval is determined by the applicable
                government agencies.
              </p>
            </div>
            <div>
              <LeadForm source="startup" defaultService="Trucking Company Startup" submitLabel="START MY TRUCKING COMPANY" />
            </div>
          </div>
        </div>
      </section>

      <CTABand eyebrow="New authority? Start here." title="Ready to build your trucking company? Let's start." button="START MY TRUCKING COMPANY" />
    </div>
  );
}
